<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { useSnackbar } from '@/composables/useSnackbar'

// Identification Queue — approval UI for `/iam` chatbot claims.
//
// A user types `/iam` in Messenger or WhatsApp, the bot collects
// name/phone/email/address and POSTs to /api/public/chatbot/identity-claim,
// creating a `contact_chat_identities` row with no contact_id yet. This
// page lists those pending rows + suggested existing Contact matches
// (phone tail, normalized name) so the seller can either link to an
// existing contact or create a new one. Either path runs
// markVerified() which assigns a client_id and DMs the user.
//
// URL: /shipment/customers/identification-queue
// API: /api/shipment/customers/chat-identity-claims*

definePage({
  meta: {
    action: 'read',
    subject: 'Customers',
    navActiveLink: 'shipment-customers-identification-queue',
  },
})

const { t } = useI18n()
const router = useRouter()
const { success, error: snackError } = useSnackbar()

const claims = ref([])
const loading = ref(false)
const totalClaims = ref(0)
const currentPage = ref(1)
const perPage = ref(25)
const channelFilter = ref(null)

// Pending queue vs processed-claims history vs parcel-claim history.
const tab = ref('pending')           // 'pending' | 'history' | 'claims'
const history = ref([])
const historyLoading = ref(false)
const historyTotal = ref(0)
const historyPage = ref(1)

// Parcel-claim audit (pending_parcel_claims, all statuses).
const parcelClaims = ref([])
const parcelClaimsLoading = ref(false)
const parcelClaimsTotal = ref(0)
const parcelClaimsPage = ref(1)

const expandedClaim = ref(null) // claim id whose suggestions are expanded
const approving = ref(null)     // claim id currently being approved (for spinner)
const rejectDialog = ref(false)
const claimToReject = ref(null)
const createDialog = ref(false)
const claimToCreate = ref(null)
const linkDialog = ref(false)
const linkContext = ref(null)   // { claim, contact, backfill }

// channel filter options — `title` is computed so it stays in the active
// locale even after the user switches language without reloading.
const channelOptions = computed(() => [
  { title: t('shipment.chat_id_channel_all'), value: null },
  { title: 'Messenger', value: 'messenger' },
  { title: 'WhatsApp', value: 'whatsapp' },
  { title: 'Web',       value: 'web' },
])

const channelIcon = channel => ({
  messenger: 'tabler-brand-messenger',
  whatsapp: 'tabler-brand-whatsapp',
  web: 'tabler-world',
}[channel] ?? 'tabler-message-circle')

const channelColor = channel => ({
  messenger: 'info',
  whatsapp: 'success',
  web: 'secondary',
}[channel] ?? 'default')

// Company the claim came in on — which bot/page the customer used.
// Lets the seller tell Godana Tracking vs Going Beyond Tracking at a glance.
const companyChip = company => ({
  godana:       { label: 'Godana Tracking',       color: 'primary', icon: 'tabler-building-store' },
  going_beyond: { label: 'Going Beyond Tracking',  color: 'deep-purple', icon: 'tabler-building-store' },
}[company] ?? { label: company || 'Godana Tracking', color: 'default', icon: 'tabler-building-store' })

const reasonChip = reason => ({
  same_phone_tail:      { label: t('shipment.chat_id_match_phone'),       color: 'success', icon: 'tabler-phone-check' },
  same_normalized_name: { label: t('shipment.chat_id_match_name'),        color: 'warning', icon: 'tabler-user-check' },
  phone_like:           { label: t('shipment.chat_id_match_phone_fuzzy'), color: 'info',    icon: 'tabler-phone' },
}[reason] ?? { label: reason, color: 'default', icon: 'tabler-help' })

const formatDate = iso => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return iso }
}

const fetchClaims = async () => {
  loading.value = true
  try {
    const params = { page: currentPage.value, per_page: perPage.value }
    if (channelFilter.value) params.channel = channelFilter.value

    const { data } = await axios.get('/shipment/customers/chat-identity-claims', { params })
    if (data.success) {
      claims.value = data.data
      totalClaims.value = data.meta?.total ?? data.data.length
    } else {
      snackError(data.message || 'Failed to load claims')
    }
  } catch (e) {
    console.error('fetchClaims', e)
    snackError(e.response?.data?.message || 'Failed to load claims')
  } finally {
    loading.value = false
  }
}

// Processed claims (approved/rejected) for the history tab.
const fetchHistory = async () => {
  historyLoading.value = true
  try {
    const params = { page: historyPage.value, per_page: perPage.value }
    if (channelFilter.value) params.channel = channelFilter.value

    const { data } = await axios.get('/shipment/customers/chat-identity-claims/history', { params })
    if (data.success) {
      history.value = data.data
      historyTotal.value = data.meta?.total ?? data.data.length
    } else {
      snackError(data.message || 'Failed to load history')
    }
  } catch (e) {
    console.error('fetchHistory', e)
    snackError(e.response?.data?.message || 'Failed to load history')
  } finally {
    historyLoading.value = false
  }
}

// Parcel-claim audit feed (all statuses).
const fetchParcelClaims = async () => {
  parcelClaimsLoading.value = true
  try {
    const params = { page: parcelClaimsPage.value, per_page: perPage.value }
    if (channelFilter.value) params.channel = channelFilter.value

    const { data } = await axios.get('/shipment/auto-match-notifications/history', { params })
    if (data.success) {
      parcelClaims.value = data.data
      parcelClaimsTotal.value = data.meta?.total ?? data.data.length
    } else {
      snackError(data.message || 'Failed to load parcel claims')
    }
  } catch (e) {
    console.error('fetchParcelClaims', e)
    snackError(e.response?.data?.message || 'Failed to load parcel claims')
  } finally {
    parcelClaimsLoading.value = false
  }
}

// Load the active tab's data when the tab or filter changes.
const onTabChange = () => {
  if (tab.value === 'history') fetchHistory()
  else if (tab.value === 'claims') fetchParcelClaims()
  else fetchClaims()
}

// Status chip for a parcel claim (pending / matched / rejected / expired).
const claimStatusChip = status => ({
  matched:  { label: t('shipment.claim_status_matched'),  color: 'success', icon: 'tabler-package-import' },
  pending:  { label: t('shipment.claim_status_pending'),  color: 'warning', icon: 'tabler-clock' },
  rejected: { label: t('shipment.claim_status_rejected'), color: 'error',   icon: 'tabler-alert-triangle' },
  expired:  { label: t('shipment.claim_status_expired'),  color: 'default', icon: 'tabler-circle-x' },
}[status] ?? { label: status, color: 'default', icon: 'tabler-help' })

const outcomeChip = status => ({
  approved: { label: t('shipment.chat_id_hist_approved'), color: 'success', icon: 'tabler-check' },
  rejected: { label: t('shipment.chat_id_hist_rejected'), color: 'error',   icon: 'tabler-x' },
}[status] ?? { label: status, color: 'default', icon: 'tabler-help' })

const toggleExpand = id => {
  expandedClaim.value = expandedClaim.value === id ? null : id
}

// Open the link confirmation dialog instead of linking immediately.
// Lets the seller toggle backfill, see if they're about to overwrite
// a phone, and bail if they pick the wrong contact in a phone-tail
// collision case.
const openLinkDialog = (claim, suggestion) => {
  linkContext.value = {
    claim,
    contact: suggestion.contact,
    reason: suggestion.reason,
    backfill: true,
    // Per-field overwrite intent. Filled in by clicking "écraser" chips
    // in the dialog. Without these, backfill only fills empty/placeholder
    // values; an existing non-empty value is left alone.
    overwrite: {
      name: false,
      phone: false,
      email: false,
      address: false,
    },
  }
  linkDialog.value = true
}

// Phone-tail-match count > 1 means multiple distinct contacts share
// the same phone tail (family shared phone, business line, etc).
// Shows a warning chip on the claim header.
const hasPhoneCollision = claim => (claim.phone_tail_match_count ?? 0) > 1

// What the contact's existing field looks like — used to render
// "you'll overwrite X with Y" lines in the link dialog. Returns null
// when the contact field is empty (so backfill is a pure win).
const fieldDiff = (contact, payload, field, isPlaceholder = false) => {
  const existing = contact?.[field]
  const claimed  = payload?.[field]
  if (!claimed) return null
  if (!existing || isPlaceholder) {
    return { kind: 'fill',     existing: existing || null, claimed }
  }
  if (existing === claimed) return null
  return { kind: 'overwrite', existing, claimed }
}

// Phone placeholders the warehouse uses when registering an unknown
// customer — treat them as empty for diff display.
const isPlaceholderPhone = v => {
  if (!v) return true
  if (typeof v !== 'string') return false
  if (v.startsWith('BC:') || v.startsWith('BC-')) return true
  const digits = v.replace(/\D/g, '')
  if (/^0+$/.test(digits)) return true
  if (digits.length < 8) return true
  return false
}

// Name placeholders from PL imports — short codes / sentinel values
// that should be silently replaced by the claim's real name without
// requiring operator confirmation.
const isPlaceholderName = v => {
  if (!v) return true
  if (typeof v !== 'string') return false
  const n = v.trim()
  if (n === '' || n === '???') return true
  if (/^(CL-|UNKNOWN-|UNKNOWN_)/i.test(n)) return true
  return false
}

const linkDiffs = computed(() => {
  const ctx = linkContext.value
  if (!ctx) return []
  const c = ctx.contact
  const p = ctx.claim?.claim_payload || {}
  return [
    { field: 'name',    ...fieldDiff(c, p, 'name',  isPlaceholderName(c?.name)) },
    { field: 'phone',   ...fieldDiff(c, p, 'phone', isPlaceholderPhone(c?.phone)) },
    { field: 'email',   ...fieldDiff(c, p, 'email') },
    { field: 'address', ...fieldDiff(c, p, 'address') },
  ].filter(d => d.kind)
})

// Toggle the "écraser" intent for a field — passed to the backend so it
// knows whether to overwrite an existing non-empty value.
const toggleOverwrite = (field) => {
  if (!linkContext.value) return
  linkContext.value.overwrite[field] = !linkContext.value.overwrite[field]
}

const confirmLink = async () => {
  const ctx = linkContext.value
  if (!ctx) return
  approving.value = ctx.claim.id
  try {
    const { data } = await axios.post(
      `/shipment/customers/chat-identity-claims/${ctx.claim.id}/approve-link`,
      {
        contact_id: ctx.contact.id,
        backfill: ctx.backfill,
        overwrite_name:    !!ctx.overwrite?.name,
        overwrite_phone:   !!ctx.overwrite?.phone,
        overwrite_email:   !!ctx.overwrite?.email,
        overwrite_address: !!ctx.overwrite?.address,
      },
    )
    if (data.success) {
      success(`Linked claim to ${data.data?.contact?.name || `#${ctx.contact.id}`} (${data.data?.contact?.client_id || ''}) — bot notified`)
      linkDialog.value = false
      linkContext.value = null
      await fetchClaims()
    } else {
      snackError(data.message || 'Failed to approve')
    }
  } catch (e) {
    console.error('approveLink', e)
    snackError(e.response?.data?.message || 'Failed to approve')
  } finally {
    approving.value = null
  }
}

const openCreateDialog = claim => {
  claimToCreate.value = claim
  createDialog.value = true
}

const approveCreate = async () => {
  if (!claimToCreate.value) return
  approving.value = claimToCreate.value.id
  try {
    const { data } = await axios.post(
      `/shipment/customers/chat-identity-claims/${claimToCreate.value.id}/approve-create`,
    )
    if (data.success) {
      const c = data.data?.contact
      success(`Created contact #${c?.id} (${c?.client_id}) — bot notified`)
      createDialog.value = false
      claimToCreate.value = null
      await fetchClaims()
    } else {
      snackError(data.message || 'Failed to create')
    }
  } catch (e) {
    console.error('approveCreate', e)
    snackError(e.response?.data?.message || 'Failed to create')
  } finally {
    approving.value = null
  }
}

const openRejectDialog = claim => {
  claimToReject.value = claim
  rejectDialog.value = true
}

const rejectClaim = async () => {
  if (!claimToReject.value) return
  approving.value = claimToReject.value.id
  try {
    const { data } = await axios.delete(
      `/shipment/customers/chat-identity-claims/${claimToReject.value.id}`,
    )
    if (data.success) {
      success('Claim rejected')
      rejectDialog.value = false
      claimToReject.value = null
      await fetchClaims()
    } else {
      snackError(data.message || 'Failed to reject')
    }
  } catch (e) {
    console.error('rejectClaim', e)
    snackError(e.response?.data?.message || 'Failed to reject')
  } finally {
    approving.value = null
  }
}

const goToCustomer = id => {
  // Customers list is filterable by id query for quick lookup; if a
  // dedicated detail page exists, prefer that. Today we just link
  // to the customer list — the seller can search by ID there.
  router.push({ name: 'shipment-customers', query: { id } })
}

onMounted(fetchClaims)
</script>

<template>
  <div>
    <VCard>
      <VCardTitle class="d-flex align-center flex-wrap py-4 gap-3">
        <div class="d-flex align-center gap-2">
          <VIcon icon="tabler-id-badge-2" />
          <h3 class="text-h4 mb-0">
            {{ t('shipment.chat_id_queue_title') }}
          </h3>
        </div>

        <VSpacer />

        <VSelect
          v-model="channelFilter"
          :items="channelOptions"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 220px;"
          @update:model-value="onTabChange"
        />

        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="tabler-refresh"
          :loading="loading || historyLoading"
          @click="onTabChange"
        >
          {{ t('actions.refresh') }}
        </VBtn>
      </VCardTitle>

      <VTabs v-model="tab" class="px-4" @update:model-value="onTabChange">
        <VTab value="pending">
          <VIcon icon="tabler-inbox" start size="18" />
          {{ t('shipment.chat_id_tab_pending') }}
        </VTab>
        <VTab value="history">
          <VIcon icon="tabler-history" start size="18" />
          {{ t('shipment.chat_id_tab_history') }}
        </VTab>
        <VTab value="claims">
          <VIcon icon="tabler-package" start size="18" />
          {{ t('shipment.chat_id_tab_claims') }}
        </VTab>
      </VTabs>
      <VDivider />

      <VCardText v-if="tab === 'pending'" class="pt-4">
        <div class="text-body-2 text-medium-emphasis mb-3">
          {{ t('shipment.chat_id_queue_intro') }}
        </div>

        <VAlert
          v-if="!loading && claims.length === 0"
          type="success"
          variant="tonal"
          class="mb-4"
        >
          <VIcon icon="tabler-checks" class="me-2" />
          {{ t('shipment.chat_id_queue_empty') }}
        </VAlert>

        <VProgressLinear v-if="loading" indeterminate color="primary" class="mb-3" />

        <div class="d-flex flex-column gap-3">
          <VCard
            v-for="claim in claims"
            :key="claim.id"
            variant="outlined"
            border
          >
            <VCardText>
              <!-- Header: channel + submitted date + actions -->
              <div class="d-flex align-center flex-wrap gap-2 mb-3">
                <VChip
                  :color="channelColor(claim.channel)"
                  variant="tonal"
                  size="small"
                >
                  <VIcon :icon="channelIcon(claim.channel)" start size="16" />
                  {{ claim.channel }}
                </VChip>

                <VChip
                  :color="companyChip(claim.company).color"
                  variant="tonal"
                  size="small"
                >
                  <VIcon :icon="companyChip(claim.company).icon" start size="16" />
                  {{ companyChip(claim.company).label }}
                </VChip>

                <VChip variant="tonal" size="small" color="info">
                  <VIcon icon="tabler-clock" start size="16" />
                  {{ formatDate(claim.created_at) }}
                </VChip>

                <code class="text-caption text-medium-emphasis">
                  PSID: {{ claim.psid }}
                </code>

                <VChip
                  v-if="hasPhoneCollision(claim)"
                  color="warning"
                  size="small"
                  variant="tonal"
                >
                  <VIcon icon="tabler-alert-triangle" start size="16" />
                  {{ t('shipment.chat_id_phone_collision', { count: claim.phone_tail_match_count }) }}
                </VChip>

                <VSpacer />

                <VBtn
                  variant="tonal"
                  color="error"
                  size="small"
                  prepend-icon="tabler-x"
                  :loading="approving === claim.id"
                  @click="openRejectDialog(claim)"
                >
                  {{ t('shipment.chat_id_reject') }}
                </VBtn>

                <VBtn
                  variant="tonal"
                  color="primary"
                  size="small"
                  prepend-icon="tabler-user-plus"
                  :loading="approving === claim.id"
                  @click="openCreateDialog(claim)"
                >
                  {{ t('shipment.chat_id_create_new_contact') }}
                </VBtn>
              </div>

              <!-- Claim payload -->
              <VRow dense>
                <VCol cols="12" md="6">
                  <div class="text-caption text-medium-emphasis">{{ t('shipment.chat_id_claimed_name') }}</div>
                  <div class="text-body-1 font-weight-medium">
                    {{ claim.claim_payload?.name || '—' }}
                  </div>
                </VCol>
                <VCol cols="12" md="6">
                  <div class="text-caption text-medium-emphasis">{{ t('shipment.chat_id_claimed_phone') }}</div>
                  <div class="text-body-1 font-weight-medium">
                    {{ claim.claim_payload?.phone || '—' }}
                    <span v-if="claim.phone_tail" class="text-caption text-medium-emphasis ms-2">
                      {{ t('shipment.chat_id_claimed_tail', { tail: claim.phone_tail }) }}
                    </span>
                  </div>
                </VCol>
                <VCol v-if="claim.claim_payload?.email" cols="12" md="6">
                  <div class="text-caption text-medium-emphasis">{{ t('shipment.chat_id_email') }}</div>
                  <div class="text-body-2">{{ claim.claim_payload.email }}</div>
                </VCol>
                <VCol v-if="claim.claim_payload?.address" cols="12" md="6">
                  <div class="text-caption text-medium-emphasis">{{ t('shipment.chat_id_address') }}</div>
                  <div class="text-body-2">{{ claim.claim_payload.address }}</div>
                </VCol>
                <VCol
                  v-if="typeof claim.claim_payload?.sms_opt_in === 'boolean'"
                  cols="12"
                >
                  <VChip
                    :color="claim.claim_payload.sms_opt_in ? 'success' : 'default'"
                    variant="tonal"
                    size="small"
                  >
                    <VIcon
                      :icon="claim.claim_payload.sms_opt_in ? 'tabler-bell' : 'tabler-bell-off'"
                      start
                      size="16"
                    />
                    {{ claim.claim_payload.sms_opt_in
                      ? t('shipment.chat_id_sms_yes')
                      : t('shipment.chat_id_sms_no') }}
                  </VChip>
                </VCol>
              </VRow>

              <VDivider class="my-3" />

              <!-- Suggestions -->
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-2">
                  <VIcon icon="tabler-search" size="18" class="me-1" />
                  {{ t('shipment.chat_id_suggested_matches') }}
                  <VChip
                    v-if="claim.suggestion_count > 0"
                    color="primary"
                    size="x-small"
                    class="ms-2"
                  >
                    {{ claim.suggestion_count }}
                  </VChip>
                </div>
              </div>

              <VAlert
                v-if="!claim.suggestions?.length"
                type="info"
                variant="tonal"
                density="compact"
              >
                {{ t('shipment.chat_id_no_matches_create_new') }}
              </VAlert>

              <VTable v-else density="compact" hover>
                <thead>
                  <tr>
                    <th style="width: 90px;">{{ t('shipment.chat_id_col_match') }}</th>
                    <th>{{ t('shipment.chat_id_col_contact') }}</th>
                    <th>{{ t('shipment.chat_id_col_phone') }}</th>
                    <th style="width: 100px;">{{ t('shipment.chat_id_col_parcels') }}</th>
                    <th style="width: 110px;">{{ t('shipment.chat_id_col_status') }}</th>
                    <th style="width: 220px;" class="text-end">{{ t('shipment.chat_id_col_action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in claim.suggestions" :key="s.contact.id">
                    <td>
                      <VChip
                        :color="reasonChip(s.reason).color"
                        size="x-small"
                        variant="tonal"
                      >
                        <VIcon :icon="reasonChip(s.reason).icon" start size="14" />
                        {{ reasonChip(s.reason).label }}
                      </VChip>
                    </td>
                    <td>
                      <div class="font-weight-medium">{{ s.contact.name }}</div>
                      <div class="text-caption text-medium-emphasis">
                        #{{ s.contact.id }}
                        <span v-if="s.contact.client_id"> · {{ s.contact.client_id }}</span>
                      </div>
                    </td>
                    <td>{{ s.contact.phone || '—' }}</td>
                    <td>
                      <VChip
                        :color="(s.contact.incoming_parcels_count ?? 0) > 0 ? 'success' : 'default'"
                        size="x-small"
                        variant="tonal"
                      >
                        {{ s.contact.incoming_parcels_count ?? 0 }}
                      </VChip>
                    </td>
                    <td>
                      <VChip
                        :color="{
                          verified: 'success',
                          pending: 'warning',
                          unidentified: 'info',
                          merged: 'default',
                        }[s.contact.identity_status] || 'default'"
                        size="x-small"
                        variant="tonal"
                      >
                        {{ s.contact.identity_status }}
                      </VChip>
                    </td>
                    <td class="text-end">
                      <VBtn
                        size="small"
                        color="primary"
                        variant="tonal"
                        prepend-icon="tabler-link"
                        :loading="approving === claim.id"
                        :disabled="s.contact.identity_status === 'merged'"
                        @click="openLinkDialog(claim, s)"
                      >
                        {{ t('shipment.chat_id_link_verify') }}
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
            </VCardText>
          </VCard>
        </div>

        <!-- Pagination -->
        <div v-if="totalClaims > perPage" class="d-flex justify-center mt-4">
          <VPagination
            v-model="currentPage"
            :length="Math.ceil(totalClaims / perPage)"
            :total-visible="7"
            @update:model-value="fetchClaims"
          />
        </div>
      </VCardText>

      <!-- History tab: processed (approved/rejected) claims -->
      <VCardText v-else-if="tab === 'history'" class="pt-4">
        <div class="text-body-2 text-medium-emphasis mb-3">
          {{ t('shipment.chat_id_history_intro') }}
        </div>

        <VProgressLinear v-if="historyLoading" indeterminate color="primary" class="mb-3" />

        <VAlert
          v-if="!historyLoading && history.length === 0"
          type="info"
          variant="tonal"
        >
          {{ t('shipment.chat_id_history_empty') }}
        </VAlert>

        <VTable v-else density="compact" hover>
          <thead>
            <tr>
              <th>{{ t('shipment.chat_id_hist_col_outcome') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_company') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_channel') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_customer') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_linked') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_reviewed') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="h in history" :key="h.id">
              <td>
                <VChip :color="outcomeChip(h.status).color" size="x-small" variant="tonal">
                  <VIcon :icon="outcomeChip(h.status).icon" start size="14" />
                  {{ outcomeChip(h.status).label }}
                </VChip>
              </td>
              <td>
                <VChip :color="companyChip(h.company).color" size="x-small" variant="tonal">
                  {{ companyChip(h.company).label }}
                </VChip>
              </td>
              <td>
                <VChip :color="channelColor(h.channel)" size="x-small" variant="tonal">
                  <VIcon :icon="channelIcon(h.channel)" start size="14" />
                  {{ h.channel }}
                </VChip>
              </td>
              <td>
                <div class="font-weight-medium">{{ h.name || '—' }}</div>
                <div class="text-caption text-medium-emphasis">{{ h.phone || '' }}</div>
              </td>
              <td>
                <template v-if="h.contact">
                  <div class="font-weight-medium">{{ h.contact.name }}</div>
                  <div class="text-caption text-medium-emphasis">{{ h.contact.client_id || ('#' + h.contact.id) }}</div>
                </template>
                <span v-else class="text-medium-emphasis">—</span>
              </td>
              <td class="text-caption">{{ formatDate(h.reviewed_at) }}</td>
            </tr>
          </tbody>
        </VTable>

        <div v-if="historyTotal > perPage" class="d-flex justify-center mt-4">
          <VPagination
            v-model="historyPage"
            :length="Math.ceil(historyTotal / perPage)"
            :total-visible="7"
            @update:model-value="fetchHistory"
          />
        </div>
      </VCardText>

      <!-- Parcel-claims tab: full pending_parcel_claims audit, all statuses -->
      <VCardText v-else-if="tab === 'claims'" class="pt-4">
        <div class="text-body-2 text-medium-emphasis mb-3">
          {{ t('shipment.claim_audit_intro') }}
        </div>

        <VProgressLinear v-if="parcelClaimsLoading" indeterminate color="primary" class="mb-3" />

        <VAlert
          v-if="!parcelClaimsLoading && parcelClaims.length === 0"
          type="info"
          variant="tonal"
        >
          {{ t('shipment.claim_audit_empty') }}
        </VAlert>

        <VTable v-else density="compact" hover>
          <thead>
            <tr>
              <th>{{ t('shipment.claim_col_status') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_company') }}</th>
              <th>{{ t('shipment.chat_id_hist_col_channel') }}</th>
              <th>{{ t('shipment.claim_col_customer') }}</th>
              <th>{{ t('shipment.claim_col_barcode') }}</th>
              <th>{{ t('shipment.claim_col_submitted') }}</th>
              <th>{{ t('shipment.claim_col_matched') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in parcelClaims" :key="c.id">
              <td>
                <VChip :color="claimStatusChip(c.status).color" size="x-small" variant="tonal">
                  <VIcon :icon="claimStatusChip(c.status).icon" start size="14" />
                  {{ claimStatusChip(c.status).label }}
                </VChip>
              </td>
              <td>
                <VChip :color="companyChip(c.company).color" size="x-small" variant="tonal">
                  {{ companyChip(c.company).label }}
                </VChip>
              </td>
              <td>
                <VChip :color="channelColor(c.channel)" size="x-small" variant="tonal">
                  <VIcon :icon="channelIcon(c.channel)" start size="14" />
                  {{ c.channel }}
                </VChip>
              </td>
              <td>
                <template v-if="c.contact">
                  <div class="font-weight-medium">{{ c.contact.name }}</div>
                  <div class="text-caption text-medium-emphasis">{{ c.contact.client_id || ('#' + c.contact.id) }}</div>
                </template>
                <span v-else class="text-medium-emphasis">—</span>
              </td>
              <td>
                <code class="text-caption">{{ c.barcode }}</code>
                <div v-if="c.matched_parcel && c.matched_parcel.barcode !== c.barcode" class="text-caption text-medium-emphasis">
                  → {{ c.matched_parcel.barcode }}
                </div>
              </td>
              <td class="text-caption">{{ formatDate(c.created_at) }}</td>
              <td class="text-caption">{{ c.matched_at ? formatDate(c.matched_at) : '—' }}</td>
            </tr>
          </tbody>
        </VTable>

        <div v-if="parcelClaimsTotal > perPage" class="d-flex justify-center mt-4">
          <VPagination
            v-model="parcelClaimsPage"
            :length="Math.ceil(parcelClaimsTotal / perPage)"
            :total-visible="7"
            @update:model-value="fetchParcelClaims"
          />
        </div>
      </VCardText>
    </VCard>

    <!-- Link & verify confirmation (with backfill option) -->
    <VDialog v-model="linkDialog" max-width="560">
      <VCard v-if="linkContext">
        <VCardTitle>{{ t('shipment.chat_id_link_dialog_title') }}</VCardTitle>
        <VCardText>
          <p class="mb-3">
            {{ t('shipment.chat_id_link_dialog_body', {
              name: linkContext.contact?.name,
              id:   linkContext.contact?.id,
            }) }}
          </p>

          <VAlert
            v-if="hasPhoneCollision(linkContext.claim)"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ t('shipment.chat_id_link_collision_warning', {
              count: linkContext.claim.phone_tail_match_count,
            }) }}
          </VAlert>

          <VAlert
            v-if="linkContext.contact?.identity_status === 'verified'"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ t('shipment.chat_id_link_already_verified', {
              client_id: linkContext.contact?.client_id,
            }) }}
          </VAlert>

          <!-- Backfill section -->
          <VCheckbox
            v-model="linkContext.backfill"
            :label="t('shipment.chat_id_backfill_label')"
            density="compact"
            hide-details
            class="mb-2"
          />

          <div v-if="linkContext.backfill && linkDiffs.length" class="ms-8">
            <div
              v-for="d in linkDiffs"
              :key="d.field"
              class="text-body-2 mb-1"
            >
              <VIcon
                :icon="d.kind === 'fill' ? 'tabler-plus' : 'tabler-arrow-right'"
                size="14"
                :color="d.kind === 'fill' ? 'success' : 'warning'"
                class="me-1"
              />
              <strong class="text-capitalize">{{ {
                name:    t('shipment.chat_id_name'),
                phone:   t('shipment.chat_id_col_phone'),
                email:   t('shipment.chat_id_email'),
                address: t('shipment.chat_id_address'),
              }[d.field] || d.field }}:</strong>
              <span v-if="d.kind === 'fill'" class="text-medium-emphasis">
                <em v-if="!d.existing">{{ t('shipment.chat_id_diff_empty') }}</em>
                <span v-else>{{ t('shipment.chat_id_diff_placeholder') }} <em>{{ d.existing }}</em></span>
                → <strong>{{ d.claimed }}</strong>
              </span>
              <span v-else class="text-medium-emphasis">
                <em>{{ d.existing }}</em> → <strong>{{ d.claimed }}</strong>
                <VChip
                  :color="linkContext.overwrite?.[d.field] ? 'warning' : 'default'"
                  :variant="linkContext.overwrite?.[d.field] ? 'flat' : 'tonal'"
                  size="x-small"
                  class="ms-2 cursor-pointer"
                  @click.stop="toggleOverwrite(d.field)"
                >
                  <VIcon
                    :icon="linkContext.overwrite?.[d.field] ? 'tabler-check' : 'tabler-pencil'"
                    size="12"
                    class="me-1"
                  />
                  {{ t('shipment.chat_id_diff_overwrite') }}
                </VChip>
              </span>
            </div>
          </div>
          <div
            v-else-if="linkContext.backfill"
            class="ms-8 text-body-2 text-medium-emphasis"
          >
            {{ t('shipment.chat_id_backfill_nothing') }}
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="linkDialog = false">
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            variant="elevated"
            :loading="approving === linkContext?.claim?.id"
            @click="confirmLink"
          >
            {{ t('shipment.chat_id_link_verify') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Reject confirmation -->
    <VDialog v-model="rejectDialog" max-width="500">
      <VCard>
        <VCardTitle>{{ t('shipment.chat_id_reject_dialog_title') }}</VCardTitle>
        <VCardText>
          <p class="mb-2">
            {{ t('shipment.chat_id_reject_dialog_body', {
              name:  claimToReject?.claim_payload?.name,
              phone: claimToReject?.claim_payload?.phone,
            }) }}
          </p>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ t('shipment.chat_id_reject_dialog_hint') }}
          </p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="rejectDialog = false">
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="error"
            variant="elevated"
            :loading="approving === claimToReject?.id"
            @click="rejectClaim"
          >
            {{ t('shipment.chat_id_reject') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Create new contact confirmation -->
    <VDialog v-model="createDialog" max-width="500">
      <VCard>
        <VCardTitle>{{ t('shipment.chat_id_create_dialog_title') }}</VCardTitle>
        <VCardText>
          <p class="mb-3">
            {{ t('shipment.chat_id_create_dialog_body') }}
          </p>
          <VList density="compact" class="bg-surface-light rounded">
            <VListItem>
              <VListItemTitle>{{ claimToCreate?.claim_payload?.name }}</VListItemTitle>
              <VListItemSubtitle>
                {{ claimToCreate?.claim_payload?.phone }}
                <span v-if="claimToCreate?.claim_payload?.email">
                  · {{ claimToCreate.claim_payload.email }}
                </span>
              </VListItemSubtitle>
              <VListItemSubtitle v-if="claimToCreate?.claim_payload?.address">
                {{ claimToCreate.claim_payload.address }}
              </VListItemSubtitle>
            </VListItem>
          </VList>
          <VAlert
            v-if="claimToCreate?.suggestion_count > 0"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            <VIcon icon="tabler-alert-triangle" start size="18" />
            {{ t('shipment.chat_id_create_warning_existing_match', {
              count: claimToCreate.suggestion_count,
            }) }}
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="createDialog = false">
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            variant="elevated"
            :loading="approving === claimToCreate?.id"
            @click="approveCreate"
          >
            {{ t('shipment.chat_id_create_verify') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

